<template>
  <div
    id="bulk-invoice-print-root"
    class="bulk-invoice-print-wrapper"
  >
    <div
      v-if="isLoading"
      class="loading-container"
    >
      <b-spinner
        variant="primary"
        class="loading-spinner"
      />
      <p class="mt-2 mb-0">
        Chargement des factures...
      </p>
    </div>

    <div
      v-else
      class="bulk-invoices-container"
    >
      <div
        v-for="(invoice, index) in invoices"
        :key="invoice.id"
        :class="['bulk-invoice-page', { 'page-break': index < invoices.length - 1 }]"
      >
        <div class="invoice-content">
          <b-card class="invoice-card">
            <!-- Header Section -->
            <div class="invoice-header-box">
              <div class="header-inner d-flex justify-content-between align-items-center">
                <h1 class="invoice-title mb-0">
                  Règlement N° <span class="invoice-number">{{ invoice.id }}</span>
                </h1>
                <div class="invoice-date">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  <span>{{ formatFn(invoice.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Company & Supplier Information -->
            <b-row class="company-info-section">
              <!-- Company Info Card -->
              <b-col
                cols="12"
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="info-card company-card">
                  <div class="info-header">
                    <feather-icon
                      icon="HomeIcon"
                      size="20"
                      class="mr-2 icon-dark"
                    />
                    <h5 class="mb-0 card-title-text">
                      Notre Société
                    </h5>
                  </div>
                  <div class="info-divider" />
                  <div class="info-content">
                    <div class="company-name-block">
                      <strong class="company-name">{{ company.name || 'B2C Delivery' }}</strong>
                      <span class="info-subtitle">Société de livraison des colis</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Matricule fiscale:</span>
                      <span class="info-value">{{ company.tax_identification_number || '1870704/A/P/M/000' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Adresse:</span>
                      <span class="info-value">{{ company.address || 'Hammamet' }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Téléphone:</span>
                      <span class="info-value">{{ company.phone1 || '55030005' }}</span>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Supplier Info Card -->
              <b-col
                cols="12"
                md="6"
              >
                <div
                  v-if="invoice.supplier"
                  class="info-card supplier-card"
                >
                  <div class="info-header">
                    <feather-icon
                      icon="UserIcon"
                      size="20"
                      class="mr-2 icon-dark"
                    />
                    <h5 class="mb-0 card-title-text">
                      Fournisseur
                    </h5>
                  </div>
                  <div class="info-divider supplier-divider" />
                  <div class="info-content">
                    <div class="company-name-block">
                      <strong class="company-name">{{ invoice.supplier.username }}</strong>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Matricule fiscale:</span>
                      <span class="info-value">{{ invoice.supplier.tax_identification_number }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Adresse:</span>
                      <span class="info-value">{{ invoice.supplier.address }}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Téléphone:</span>
                      <span class="info-value">{{ invoice.supplier.phone }}</span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Summary Table Section -->
            <div class="summary-section">
              <div class="section-header-title">
                <feather-icon
                  icon="BarChartIcon"
                  size="20"
                  class="mr-2 icon-dark"
                />
                <span class="section-title-text">Résumé de la facture</span>
              </div>
              <div class="section-underline" />

              <div class="table-border-wrapper">
                <table class="modern-summary-table">
                  <thead>
                    <tr>
                      <th>Nombre de colis</th>
                      <th>Colis livré</th>
                      <th>Colis retour</th>
                      <th>Total COD</th>
                      <th>Frais de livraison</th>
                      <th>Frais</th>
                      <th>Net à payer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <span class="stat-badge-pill">{{ invoice.total_nb }}</span>
                      </td>
                      <td>
                        <span class="stat-badge-pill">{{ invoice.nb_delivered }}</span>
                      </td>
                      <td>
                        <span class="stat-badge-pill">{{ invoice.nb_return }}</span>
                      </td>
                      <td class="amount font-weight-bold">
                        {{ formatMoney(invoice.total_ttc) }} DT
                      </td>
                      <td class="amount font-weight-bold">
                        {{ formatMoney(invoice.total_delivery) }} DT
                      </td>
                      <td class="amount font-weight-bold">
                        <div
                          v-if="settlementFeeRows(invoice).length"
                          class="fee-list-container"
                        >
                          <div
                            v-for="line in settlementFeeRows(invoice)"
                            :key="line.key"
                            class="fee-item"
                          >
                            <span>{{ line.name }}:</span>
                            <strong>{{ formatMoney(line.amount) }} DT</strong>
                          </div>
                        </div>
                        <span v-else>{{ formatMoney(invoice.frais_societe_livraison || 0) }} DT</span>
                      </td>
                      <td class="amount font-weight-bold net-payer-cell">
                        {{ formatMoney(invoice.net_a_payer || invoice.total) }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Cancellation Reason Alert if applicable -->
            <b-alert
              v-if="invoice.status === 'cancelled' && invoice.cancellation_reason"
              variant="danger"
              show
              class="mt-3 mb-3"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                class="mr-1"
              />
              Motif d'annulation : {{ invoice.cancellation_reason }}
            </b-alert>

            <!-- Delivered Orders Section -->
            <div
              v-if="invoice.delivredOrder && invoice.delivredOrder.length"
              class="orders-section"
            >
              <div class="section-header-title">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="20"
                  class="mr-2 icon-dark"
                />
                <span class="section-title-text">Colis livrés</span>
              </div>
              <div class="section-underline" />

              <div class="table-border-wrapper">
                <table class="modern-orders-table">
                  <thead>
                    <tr>
                      <th class="text-left col-bl">
                        BL
                      </th>
                      <th class="text-left col-dest">
                        DESTINATAIRE
                      </th>
                      <th class="text-center col-gouv">
                        GOUVERNORAT
                      </th>
                      <th class="text-center col-extra">
                        EXTRA
                      </th>
                      <th class="text-right col-prix">
                        PRIX
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in invoice.delivredOrder"
                      :key="item.code"
                    >
                      <td class="text-left font-weight-bold text-dark">
                        {{ item.code }}
                      </td>
                      <td class="text-left">
                        {{ item.destinataire }}
                      </td>
                      <td class="text-center">
                        {{ item.gouvernorat }}
                      </td>
                      <td class="text-center">
                        {{ item.extra }}
                      </td>
                      <td class="text-right font-weight-bold text-dark">
                        {{ formatMoney(item.prix) }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Return Orders Section -->
            <div
              v-if="invoice.retour && invoice.retour.length"
              class="orders-section"
            >
              <div class="section-header-title">
                <feather-icon
                  icon="XCircleIcon"
                  size="20"
                  class="mr-2 icon-dark"
                />
                <span class="section-title-text">Colis retournés</span>
              </div>
              <div class="section-underline" />

              <div class="table-border-wrapper">
                <table class="modern-orders-table">
                  <thead>
                    <tr>
                      <th class="text-left col-bl">
                        BL
                      </th>
                      <th class="text-left col-dest">
                        DESTINATAIRE
                      </th>
                      <th class="text-center col-gouv">
                        GOUVERNORAT
                      </th>
                      <th class="text-center col-extra">
                        EXTRA
                      </th>
                      <th class="text-left col-motifs">
                        MOTIFS DE RETOUR
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in invoice.retour"
                      :key="item.code"
                    >
                      <td class="text-left font-weight-bold text-dark">
                        {{ item.code }}
                      </td>
                      <td class="text-left">
                        {{ item.destinataire }}
                      </td>
                      <td class="text-center">
                        {{ item.gouvernorat }}
                      </td>
                      <td class="text-center">
                        {{ item.extra }}
                      </td>
                      <td class="text-left">
                        <div
                          v-if="item.return_reasons && item.return_reasons.length"
                          class="return-reasons-list"
                        >
                          <div
                            v-for="(rr, rIdx) in item.return_reasons"
                            :key="rIdx"
                            class="rr-item"
                          >
                            <span class="rr-title font-weight-bold">{{ rr.reason || rr.motif }}</span>
                            <span
                              v-if="rr.note"
                              class="rr-note text-muted ml-1"
                            >({{ rr.note }})</span>
                          </div>
                        </div>
                        <span
                          v-else
                          class="text-muted italic"
                        >-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Signature Section -->
            <document-signature
              left-label="Signature du fournisseur"
              company-signature-label="Cachet et signature direx Delivery"
              class="document-signature-block"
            />
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BCard,
  BCol,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import { DocumentSignature } from '@/components/document'

export default {
  name: 'PrintAllInvoices',
  components: {
    BAlert,
    BCard,
    BCol,
    BRow,
    BSpinner,
    DocumentSignature,
  },
  data() {
    return {
      invoices: [],
      company: {},
      isLoading: true,
    }
  },
  async created() {
    this.company = storeAuth.state.company || {}
    await this.loadInvoices()
  },
  methods: {
    async loadInvoices() {
      const invoiceIds = this.$route.query.ids ? this.$route.query.ids.split(',').filter(Boolean) : []

      if (!invoiceIds.length) {
        this.$router.push({ name: 'invoice-list' })
        return
      }

      this.isLoading = true

      try {
        const responses = await Promise.all(invoiceIds.map(id => axios.get(`/api/invoices/detail/${id}/`)))
        this.invoices = responses.map(res => this.normalizeInvoice(res.data))

        this.$nextTick(() => {
          setTimeout(() => {
            window.print()
          }, 500)
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading invoices:', error)
      } finally {
        this.isLoading = false
      }
    },
    normalizeInvoice(invoice) {
      return {
        ...invoice,
        delivredOrder: (invoice.delivred_orders || []).map(this.normalizeDeliveredOrder),
        retour: (invoice.return_orders || []).map(this.normalizeReturnOrder),
      }
    },
    normalizeDeliveredOrder(item) {
      return {
        code: item.display_cross_tenant_barcode && item.cross_tenant_barcode ? item.cross_tenant_barcode : item.id,
        gouvernorat: item.governorate && item.governorate.name ? item.governorate.name : item.governorate,
        extra: item.extra_fee_value || '-',
        destinataire: item.name,
        adresse: item.address,
        prix: item.price_ttc,
        prixLivraison: item.price_delivery,
        status: 'livré',
        is_an_exchange: item.is_an_exchange,
      }
    },
    normalizeReturnOrder(item) {
      return {
        code: item.display_cross_tenant_barcode && item.cross_tenant_barcode ? item.cross_tenant_barcode : item.id,
        gouvernorat: item.governorate && item.governorate.name ? item.governorate.name : item.governorate,
        extra: item.extra_fee_value || '-',
        destinataire: item.name,
        adresse: item.address,
        prix: item.price_ttc,
        prixLivraison: item.price_delivery,
        prixRetour: item.price_return,
        status: 'retour',
        is_an_exchange: item.is_an_exchange,
        return_reasons: item.return_reasons || [],
      }
    },
    settlementFeeRows(invoice) {
      const breakdown = invoice.fee_breakdown || []

      if (breakdown.length) {
        return breakdown.map((line, index) => ({
          key: line.id || line.configuration_id || `${line.name}-${index}`,
          name: line.name || 'Frais société',
          amount: Math.abs(Number(line.calculated_amount ?? line.signed_amount ?? 0)),
        }))
      }

      const aggregateFee = Number(invoice.frais_societe_livraison || 0)
      if (!aggregateFee) return []

      return [{
        key: 'frais-societe',
        name: 'Frais société',
        amount: Math.abs(aggregateFee),
      }]
    },
    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const dayValue = day.substr(0, 2)
      const timeValue = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${dayValue.padStart(2, '0')} | ${timeValue}`
    },
    formatDateOnly(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(3)
    },
  },
}
</script>

<style lang="scss" scoped>
.bulk-invoice-print-wrapper {
  min-height: 100vh;
  padding: 2rem 1.5rem;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1d1d1b;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.bulk-invoice-page {
  max-width: 900px;
  margin: 0 auto 2.5rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #dcdcdc;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
}

.page-break {
  break-after: page;
  page-break-after: always;
}

.invoice-card {
  border-radius: 12px;
  border: none;
  background: #ffffff;
  padding: 1.75rem;
}

/* Header Box */
.invoice-header-box {
  border: 2px solid #1d1d1b;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  background: #ffffff;
}

.invoice-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1d1d1b;
  line-height: 1.2;
}

.invoice-number {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1d1d1b;
  vertical-align: baseline;
  margin-left: 2px;
}

.invoice-date {
  font-size: 0.95rem;
  color: #2d3748;
  font-weight: 500;
  display: flex;
  align-items: center;
}

/* Info Cards */
.company-info-section {
  margin-bottom: 1.75rem;
}

.info-card {
  background: #ffffff;
  border: 2px solid #1d1d1b;
  border-radius: 12px;
  padding: 1.25rem;
  height: 100%;

  &.supplier-card {
    border-left: 4px solid #114092;
  }
}

.info-header {
  display: flex;
  align-items: center;
  color: #1d1d1b;

  .card-title-text {
    font-weight: 800;
    font-size: 1.1rem;
    color: #1d1d1b;
  }

  .icon-dark {
    color: #1d1d1b;
  }
}

.info-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 0.6rem 0 0.85rem 0;

  &.supplier-divider {
    height: 2px;
    background: #1d1d1b;
  }
}

.info-content {
  font-size: 0.95rem;

  .company-name-block {
    margin-bottom: 0.5rem;
  }

  .company-name {
    font-size: 1.1rem;
    font-weight: 800;
    color: #1d1d1b;
    display: block;
  }

  .info-subtitle {
    font-size: 0.85rem;
    color: #718096;
    font-style: italic;
    display: block;
    margin-top: 0.1rem;
  }

  .info-item {
    margin-bottom: 0.35rem;
    line-height: 1.4;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-label {
    font-weight: 700;
    color: #1d1d1b;
    margin-right: 0.3rem;
  }

  .info-value {
    color: #1d1d1b;
  }
}

/* Sections & Underlines */
.summary-section,
.orders-section {
  margin-bottom: 1.75rem;
}

.section-header-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;

  .section-title-text {
    font-size: 1.25rem;
    font-weight: 800;
    color: #1d1d1b;
  }

  .icon-dark {
    color: #1d1d1b;
  }
}

.section-underline {
  height: 2px;
  background: #1d1d1b;
  margin-bottom: 1rem;
  width: 100%;
}

/* Tables */
.table-border-wrapper {
  border: 2px solid #1d1d1b;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}

.modern-summary-table,
.modern-orders-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;

  thead {
    tr {
      border-bottom: 2px solid #1d1d1b;
    }

    th {
      padding: 0.75rem 0.5rem;
      font-weight: 800;
      font-size: 0.85rem;
      color: #1d1d1b;
      text-align: center;
      vertical-align: middle;
      background: #ffffff;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 0.75rem 0.5rem;
      font-size: 0.95rem;
      color: #1d1d1b;
      text-align: center;
      vertical-align: middle;
    }
  }
}

.stat-badge-pill {
  display: inline-block;
  border: 1.5px solid #1d1d1b;
  border-radius: 6px;
  padding: 2px 10px;
  font-weight: 800;
  font-size: 0.95rem;
  color: #1d1d1b;
  background: #ffffff;
  min-width: 32px;
  text-align: center;
}

.amount {
  white-space: nowrap;
  color: #1d1d1b;
}

.net-payer-cell {
  font-weight: 800 !important;
}

.fee-list-container {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.85rem;
}

.fee-item {
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
}

/* Orders Table Column Widths & Alignments */
.modern-orders-table {
  thead th {
    font-size: 0.82rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0.75rem 1rem;
  }

  tbody td {
    padding: 0.65rem 1rem;
  }

  .col-bl { width: 15%; }
  .col-dest { width: 35%; }
  .col-gouv { width: 20%; }
  .col-extra { width: 12%; }
  .col-prix { width: 18%; }
  .col-motifs { width: 35%; }
}

.return-reasons-list {
  font-size: 0.85rem;
}

.rr-item {
  margin-bottom: 0.2rem;
}

/* Signatures Block */
.document-signature-block {
  border-top: 1px dotted #cbd5e1;
  margin-top: 2rem;
  padding-top: 1.25rem;
}

</style>

<style lang="scss">
/* Print CSS Rules - MUST be unscoped to target app shell elements outside component */
@media print {
  @page {
    size: A4 portrait;
    margin: 5mm;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    color: black !important;
    font-family: Arial, sans-serif !important;
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  body * {
    visibility: hidden !important;
  }

  /* Hide app shell elements - sidebar, navbar, footer */
  nav.header-navbar,
  .main-menu,
  .header-navbar-shadow,
  footer.footer,
  .customizer-toggle,
  .d-print-none,
  .feather-icon,
  .no-print {
    display: none !important;
  }

  /* Reset app layout containers to full width */
  #app,
  .app-content,
  .content.app-content,
  .app-content .content-wrapper,
  .app-content .content-body,
  .content-area-wrapper,
  .bulk-invoice-print-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    height: auto !important;
    background: white !important;
    overflow: visible !important;
  }

  #bulk-invoice-print-root,
  #bulk-invoice-print-root * {
    visibility: visible !important;
  }

  #bulk-invoice-print-root {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: auto !important;
    padding: 0 !important;
    background: white !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    font-family: Arial, sans-serif !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
    color: black !important;
  }

  #bulk-invoice-print-root *,
  #bulk-invoice-print-root *::before,
  #bulk-invoice-print-root *::after {
    box-sizing: border-box !important;
  }

  .bulk-invoices-container,
  .bulk-invoice-page,
  .invoice-content,
  .invoice-card {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    background: white !important;
    overflow: visible !important;
  }

  .invoice-card {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .bulk-invoice-page {
    break-after: page !important;
    page-break-after: always !important;
    padding-bottom: 5mm !important;
  }

  .bulk-invoice-page:last-child {
    break-after: auto !important;
    page-break-after: auto !important;
  }

  /* Header Box - matching Show.vue BW */
  .invoice-header-box {
    border: 2px solid black !important;
    border-radius: 0 !important;
    padding: 14px !important;
    margin-bottom: 10px !important;
    background: white !important;
    page-break-inside: avoid !important;
  }

  .invoice-title {
    font-size: 24px !important;
    font-weight: bold !important;
    color: black !important;
    margin: 0 0 4px 0 !important;
  }

  .invoice-number {
    font-size: 14px !important;
    color: black !important;
  }

  .invoice-date {
    font-size: 14px !important;
    color: black !important;
  }

  /* Company Info Section - matching Show.vue BW */
  .company-info-section {
    margin-bottom: 10px !important;
    page-break-inside: avoid !important;
  }

  .company-info-section .row {
    display: flex !important;
    flex-wrap: wrap !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .company-info-section .col-md-6 {
    flex: 0 0 50% !important;
    max-width: 50% !important;
    padding: 0 3px !important;
    box-sizing: border-box !important;
  }

  .info-card {
    border: 1px solid black !important;
    border-radius: 0 !important;
    padding: 10px !important;
    margin-bottom: 10px !important;
    background: white !important;
    min-height: 100px !important;
    page-break-inside: avoid !important;
  }

  .info-card.supplier-card {
    border-left: 1px solid black !important;
  }

  .info-header {
    font-size: 14px !important;
    font-weight: bold !important;
    color: black !important;
    margin-bottom: 8px !important;
    border-bottom: 1px solid black !important;
    padding-bottom: 3px !important;
  }

  .info-header .card-title-text {
    font-size: 14px !important;
    font-weight: bold !important;
    color: black !important;
  }

  .info-header .icon-dark {
    display: none !important;
  }

  .info-divider {
    display: none !important;
  }

  .info-content .company-name-block {
    margin-bottom: 6px !important;
  }

  .info-content .company-name {
    font-size: 14px !important;
    font-weight: bold !important;
    color: black !important;
  }

  .info-content .info-subtitle {
    font-style: italic !important;
    color: black !important;
    font-size: 12px !important;
  }

  .info-content .info-item {
    margin-bottom: 6px !important;
    font-size: 13px !important;
    color: black !important;
  }

  .info-content .info-item:last-child {
    margin-bottom: 0 !important;
  }

  .info-content .info-label {
    font-weight: bold !important;
    color: black !important;
    min-width: 80px !important;
    display: inline-block !important;
  }

  .info-content .info-value {
    color: black !important;
  }

  /* Section Headers - matching Show.vue BW */
  .summary-section,
  .orders-section {
    margin-bottom: 15px !important;
  }

  .summary-section {
    page-break-inside: avoid !important;
  }

  .section-header-title {
    margin-bottom: 4px !important;
  }

  .section-header-title .icon-dark {
    display: none !important;
  }

  .section-header-title .section-title-text {
    font-size: 16px !important;
    font-weight: bold !important;
    color: black !important;
  }

  .section-underline {
    height: 1px !important;
    background: black !important;
    margin-bottom: 10px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Tables - matching Show.vue BW */
  .table-border-wrapper {
    border: 1px solid black !important;
    border-radius: 0 !important;
    overflow: hidden !important;
    background: white !important;
    max-width: 100% !important;
  }

  .modern-summary-table,
  .modern-orders-table {
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    font-size: 14px !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }

  .modern-summary-table thead,
  .modern-orders-table thead {
    display: table-header-group !important;
  }

  .modern-summary-table thead tr,
  .modern-orders-table thead tr {
    border-bottom: 2px solid black !important;
  }

  .modern-summary-table thead th,
  .modern-orders-table thead th {
    background: transparent !important;
    color: black !important;
    padding: 8px 5px !important;
    border: none !important;
    border-bottom: 2px solid black !important;
    font-weight: bold !important;
    font-size: 14px !important;
    text-align: center !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .modern-orders-table thead th {
    text-align: left !important;
  }

  .modern-summary-table tbody tr,
  .modern-orders-table tbody tr {
    page-break-inside: avoid !important;
    border-bottom: 1px solid #ccc !important;
  }

  .modern-summary-table tbody tr:last-child,
  .modern-orders-table tbody tr:last-child {
    border-bottom: none !important;
  }

  .modern-summary-table tbody td,
  .modern-orders-table tbody td {
    padding: 6px 4px !important;
    border: none !important;
    color: black !important;
    font-size: 13px !important;
    text-align: center !important;
    page-break-inside: avoid !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .stat-badge-pill {
    background: white !important;
    color: black !important;
    border: 1px solid black !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    font-weight: bold !important;
    font-size: 13px !important;
  }

  .amount {
    font-weight: bold !important;
    color: black !important;
    font-size: 14px !important;
  }

  .net-payer-cell {
    font-weight: bold !important;
    color: black !important;
    font-size: 15px !important;
  }

  .fee-list-container {
    font-size: 12px !important;
    color: black !important;
  }

  .fee-item {
    color: black !important;
  }

  .fee-item span,
  .fee-item strong {
    color: black !important;
  }

  /* Return orders */
  .return-reasons-list {
    font-size: 12px !important;
    color: black !important;
  }

  .rr-item {
    margin-bottom: 2px !important;
    color: black !important;
  }

  .rr-title {
    color: black !important;
  }

  .rr-note {
    color: black !important;
  }

  /* Signature Block - matching Show.vue BW */
  .document-signature-block {
    margin-top: 16px !important;
    padding-top: 8px !important;
    page-break-inside: avoid !important;
    page-break-after: auto !important;
    break-after: auto !important;
    border-top: 1px dotted black !important;
  }

  .signature-section {
    margin-top: 16px !important;
    padding-top: 8px !important;
    page-break-inside: avoid !important;
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .signature-title {
    font-weight: bold !important;
    color: black !important;
    margin-bottom: 12px !important;
    font-size: 13px !important;
    text-align: center !important;
  }

  .signature-line {
    height: 50px !important;
    border-bottom: 1px solid black !important;
    margin-bottom: 3px !important;
    background: white !important;
  }

  /* Hide badge-based ml-2 decorations in print */
  .ml-2,
  .badge:not(.stat-badge-pill) {
    display: none !important;
  }

  /* Disable transitions/animations */
  * {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
}
</style>
